import CircularLoader from '@/components/utils/CircularLoader';
import { LOGO_WHITE } from '@/constants/images';
import * as P from '@/constants/paths';
import {
  PASSWORD_RECOVERY_PATH,
  REGISTRATION_LANDING,
} from '@/constants/paths';
import useLogin from '@/hooks/useLogin';
import useRegisterStore from '@/hooks/useRegistrationStore';
import { LocalStorage } from '@/utils/LocalStorage';
import { Button } from '@design-system/Button';
import { DataForm } from '@design-system/DataForm/DataForm';
import { PasswordField } from '@design-system/DataForm/PasswordField';
import { TextField } from '@design-system/DataForm/TextField';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MobileWebLogin } from './MobileWebLogin';

export const Login = () => {
  const { onValid, form, showErrorMessage, isLoading } = useLogin();
  const { registrationDone, setClearRegisterState } = useRegisterStore();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const BG_LARGE = 'https://assets.azure.viome.com/assets/image/login_new_bg.png';
  const BG_SMALL =
    'https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/login_bg_mobile_new.png';

  useEffect(() => {
    if (LocalStorage.getItem('register') && registrationDone) {
      setClearRegisterState();
    }
  }, []);

  return (
    <>
      <div className="hidden md:block">
        <div
          className="h-screen flex items-center justify-end bg-cover bg-center bg-no-repeat p-4"
          style={{
            backgroundImage: `url(${BG_LARGE})`,
            backgroundColor: '#062826',
          }}
        >
          <div className="w-full h-full md:h-[75%] min-[1025px]:h-full 2xl:[h-70%] items-center grid grid-cols-2 gap-2">
            <div className="w-[32rem] max-[942px]:w-full mx-auto self-center text-[#F4F4F5] mix-blend-plus-lighter text-opacity-70 text-left flex flex-col gap-4">
              <h1 className="typography-d1 leading-tight select-none">
                Your microbiome holds the answers to your health.
              </h1>
              <p className="typography-h4">We're just here to translate.</p>
            </div>
            <div
              className="w-full mx-auto h-full max-h- max-w-md rounded-3xl flex flex-col justify-between gap-8 overflow-hidden px-6 lg:px-12 mt-auto sm:mt-0 select-none"
              style={{
                background: 'var(--surface-overlay-primary, #42434D4D)',
                backdropFilter: 'blur(74px)',
                boxShadow: `
                0px 1px 0px 0px #FFFFFF1A inset,
                0px 4px 40px 0px #44445E29
              `,
              }}
            >
              <div className="flex-shrink-0 pt-16">
                <img src={LOGO_WHITE} className="mx-auto" alt="Viome Logo" />
              </div>

              <div
                id="login-form"
                className="flex-grow flex flex-col justify-center"
              >
                <DataForm form={form} onValid={onValid} className="gap-2">
                  <TextField
                    name="email"
                    placeholder="Email"
                    required
                    className="bg-[#00000080] border-r-stone-300 rounded-[20px] caret-white text-white placeholder:text-white/60"
                    style={{
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'inherit',
                    }}
                  />
                  <PasswordField
                    name="password"
                    placeholder="Password"
                    required
                    eyeIconColor="#C8CFDBCC"
                    className="bg-[#00000080] border-white/20 rounded-[20px] caret-white text-white placeholder:text-white/60"
                    style={{
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'inherit',
                    }}
                  />

                  {showErrorMessage && (
                    <p className="typography-btn-small text-red-400 text-center mb-4">
                      Invalid username or password
                    </p>
                  )}

                  <Button
                    type="submit"
                    className="w-full mt-8 py-3 font-sans text-primary bg-white rounded-lg hover:bg-white/80 transition-colors"
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularLoader /> : 'Login'}
                  </Button>
                  <Link
                    to={PASSWORD_RECOVERY_PATH}
                    className="block mt-4 text-sm text-center text-white hover:underline mb-8 sm:mb-14"
                  >
                    Forgot password
                  </Link>
                </DataForm>
              </div>

              <div className="flex-shrink-0 pb-8">
                <p className="text-white/60 flex flex-row gap-2 justify-center text-sm">
                  New to Viome?{' '}
                  <Link
                    to={REGISTRATION_LANDING}
                    className="text-white underline"
                  >
                    Register Your Kit
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="block md:hidden">
        {showLogin ? (
          <MobileWebLogin showLogin={(value) => setShowLogin(value)} />
        ) : (
          <div
            className="h-screen bg-cover bg-no-repeat flex flex-col px-4"
            style={{
              backgroundImage: `url(${BG_SMALL})`,
              backgroundColor: '#062826',
            }}
          >
            <div className="pl-4 max-[380px]:py-8 py-16">
              <img src={LOGO_WHITE} alt="Viome Logo" width={'36%'} />
            </div>
            <div className="h-full flex flex-col justify-between pb-2">
              <div className="w-full px-4 flex flex-col gap-2 text-white select-none text-left max-[380px]:pt-4">
                <h1 className="typography-h6 text-[2rem] font-medium leading-tight max-[400px]:w-full max-[350px]:typography-d2 max-[320px]:typography-h4">
                  Your microbiome holds the answers to your health.
                </h1>
                <p className="typography-h4 max-[350px]:typography-body">
                  We're just here to translate.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  className="w-full py-3 font-sans text-primary bg-white rounded-lg hover:bg-white/80 transition-colors"
                  onClick={() => navigate(P.REGISTRATION_LANDING)}
                >
                  Register a kit
                </Button>
                <Button
                  type="submit"
                  className="w-full py-3 font-sans typography-primary bg-black rounded-lg hover:bg-white/80 transition-colors"
                  onClick={() => setShowLogin(true)}
                >
                  Login
                </Button>
                <Button
                  type="submit"
                  className="w-full py-3 mb-2 font-sans typography-primary bg-transparent rounded-lg hover:text-white active:text-white hover:bg-transparent active:bg-transparent transition-colors"
                  disabled={isLoading}
                  onClick={() => (window.location.href = 'https://viome.com')}
                >
                  Visit viome.com
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
